/**
 * Files the owner sends from their Vault into a forge (ADR-023 A23):
 * GET /v1/forge/forges/:id/deliveries, and the send itself. The list is
 * re-read every few seconds while a delivery is still on its way (waiting
 * for the forge to start, or being written), so "In public/" shows up on its
 * own; otherwise it is read once.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';
import type { ForgeDeliverySummary } from '../types';

/** How often the list is re-read while a delivery is on its way. */
export const FORGE_DELIVERY_POLL_MS = 4000;

export interface UseForgeDeliveriesResult {
  deliveries: ForgeDeliverySummary[];
  send: (body: { holderId: string; documentIds: string[] }) => Promise<ForgeDeliverySummary>;
  isSending: boolean;
}

export function useForgeDeliveries(forgeId: string | null): UseForgeDeliveriesResult {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: forgeQueryKeys.deliveries(forgeId ?? ''),
    queryFn: async () => (await api.listForgeDeliveries(forgeId!)).deliveries,
    enabled: !!forgeId,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((d) => d.status === 'pending' || d.status === 'dispatched')
        ? FORGE_DELIVERY_POLL_MS
        : false,
  });
  const mutation = useMutation({
    mutationFn: (body: { holderId: string; documentIds: string[] }) =>
      api.sendFilesToForge(forgeId!, body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: forgeQueryKeys.deliveries(forgeId ?? '') }),
  });
  return {
    deliveries: query.data ?? [],
    send: async (body) => (await mutation.mutateAsync(body)).delivery,
    isSending: mutation.isPending,
  };
}
